import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { releaseCart, CART_KEY, EXPIRY_KEY } from "@/lib/reservation";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const POLL_INTERVAL = 2000;
const MAX_POLLS = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
  const [detail, setDetail] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    // On success page load, clear the cart + release the reservation (sold).
    localStorage.removeItem(CART_KEY);
    localStorage.removeItem(EXPIRY_KEY);
    releaseCart();
    window.dispatchEvent(new Event("cart-updated"));
  }, []);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let mounted = true;
    let handle;

    const tick = async () => {
      if (!mounted) return;
      polls.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (!mounted) return;
        setDetail(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch { /* ignore transient */ }
      if (polls.current >= MAX_POLLS) { setStatus("timeout"); return; }
      handle = setTimeout(tick, POLL_INTERVAL);
    };
    tick();
    return () => { mounted = false; clearTimeout(handle); };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg)] p-6" data-testid="payment-success-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[var(--border)] p-10 text-center shadow-lg">
        <Link to="/login" data-testid="payment-success-logo-link" className="inline-block hover:opacity-80 transition-opacity">
          <img src="/brand/mpire-logo.png" alt="MPIRE" className="w-14 h-14 mx-auto mb-4" />
        </Link>

        {status === "checking" && (
          <>
            <Loader2 className="animate-spin mx-auto text-[var(--brand)]" size={40} />
            <h1 className="text-2xl font-bold mt-4">Confirming your payment…</h1>
            <p className="text-gray-500 text-sm mt-2">This usually takes just a couple of seconds.</p>
          </>
        )}

        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-600" size={48} />
            <h1 className="text-3xl font-bold mt-4">Order confirmed</h1>
            <p className="text-gray-500 mt-2">Thank you — your payment was successful.</p>
            {detail?.amount && (
              <p className="text-xl font-bold mt-4 tabular-nums" data-testid="paid-amount">${Number(detail.amount).toFixed(2)}</p>
            )}
            {detail?.order_id && (
              <p className="text-[11px] text-gray-400 mt-2 font-mono" data-testid="order-id">Order ID: {detail.order_id.slice(0, 8)}</p>
            )}
            <p className="text-xs text-gray-500 mt-4">A confirmation email will follow shortly.</p>
            <Button asChild className="mt-6 btn-brand w-full"><Link to="/shop" data-testid="back-to-shop">Continue shopping</Link></Button>
          </>
        )}

        {status === "timeout" && (
          <>
            <Loader2 className="mx-auto text-amber-500" size={40} />
            <h1 className="text-2xl font-bold mt-4">Almost there…</h1>
            <p className="text-gray-500 mt-2 text-sm">
              We're still waiting for confirmation from Stripe. Your order will appear in the dashboard shortly.
            </p>
            <Button asChild variant="outline" className="mt-6 w-full"><Link to="/shop">Back to shop</Link></Button>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="mx-auto text-red-500" size={44} />
            <h1 className="text-2xl font-bold mt-4">Something went wrong</h1>
            <p className="text-gray-500 mt-2 text-sm">
              We couldn't confirm your payment. If your card was charged, please contact us.
            </p>
            <Button asChild variant="outline" className="mt-6 w-full"><Link to="/shop">Back to shop</Link></Button>
          </>
        )}
      </div>
    </div>
  );
}
